import React from 'react';
import { ShoppingBag, X, Trash2, MessageCircle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { useCart } from '../context/CartContext';
import { locations } from '../data/mockData';

/**
 * ConsultationCart
 *
 * Floating button (bottom-right) + modal.
 * Sends the wishlist to Quito or Guayaquil WhatsApp.
 * No checkout, no payment.
 */
const ConsultationCart = () => {
  const { items, removeItem, clear, isOpen, open, close, getSelectedDates } = useCart();

  const typeLabel = (t) => {
    switch (t) {
      case 'vestido': return 'Vestido';
      case 'bolso': return 'Bolso';
      case 'tacón': return 'Tacón';
      case 'arete': return 'Arete';
      default: return 'Producto';
    }
  };

  const sendToCity = (city) => {
    if (items.length === 0) return;
    const loc = locations.find((l) => l.city === city);
    if (!loc) return;
    const dates = getSelectedDates();
    const lines = items.map((i) => `- ${i.name} (${typeLabel(i.type)})`).join('\n');
    const HEART = '\u{1F9E1}'; // 🧡 orange heart — explicit codepoint to avoid source encoding issues
    const message =
      `Hola Morea! quiero consultar disponibilidad de estos productos ${HEART}:\n${lines}\n` +
      `Fechas seleccionadas: ${dates || 'por confirmar'}\n` +
      `Sede: ${city}`;
    const phone = String(loc.whatsapp).replace(/[^\d]/g, ''); // wa.me requires digits only
    const url = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
    close();
  };

  return (
    <>
      {/* Floating cart button */}
      <button
        onClick={open}
        data-testid="cart-fab"
        aria-label="Abrir carrito de consulta"
        className="fixed bottom-6 right-6 z-40 bg-neutral-900 hover:bg-neutral-800 text-white rounded-full w-14 h-14 sm:w-16 sm:h-16 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 flex items-center justify-center"
      >
        <ShoppingBag className="w-6 h-6" />
        {items.length > 0 && (
          <span
            data-testid="cart-count"
            className="absolute -top-1 -right-1 bg-white text-neutral-900 text-xs font-medium rounded-full min-w-[22px] h-[22px] flex items-center justify-center px-1.5 border border-neutral-900"
          >
            {items.length}
          </span>
        )}
      </button>

      <Dialog open={isOpen} onOpenChange={(o) => !o && close()}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-2xl font-serif text-neutral-900 flex items-center gap-2">
              <ShoppingBag className="w-5 h-5" />
              Mi carrito de consulta
            </DialogTitle>
            <DialogDescription className="text-neutral-600 font-light text-sm">
              Esta lista no es una reserva. Te ayudamos a confirmar disponibilidad por WhatsApp.
            </DialogDescription>
          </DialogHeader>

          {items.length === 0 ? (
            <div className="py-10 text-center">
              <ShoppingBag className="w-12 h-12 text-neutral-300 mx-auto mb-3" />
              <p className="text-neutral-600 font-light">
                Tu carrito está vacío.
              </p>
              <p className="text-neutral-500 font-light text-sm mt-2">
                Explora vestidos y accesorios y agrega tus favoritos.
              </p>
            </div>
          ) : (
            <>
              <div className="flex-1 overflow-y-auto -mx-6 px-6 py-2 divide-y divide-neutral-100">
                {items.map((item) => (
                  <div
                    key={item.id}
                    data-testid={`cart-item-${item.id}`}
                    className="py-3 flex items-center justify-between gap-3"
                  >
                    <div className="min-w-0">
                      <p className="text-neutral-900 font-light truncate">{item.name}</p>
                      <p className="text-neutral-500 text-xs uppercase tracking-wide">
                        {typeLabel(item.type)}
                      </p>
                    </div>
                    <button
                      onClick={() => removeItem(item.id)}
                      data-testid={`cart-remove-${item.id}`}
                      aria-label="Eliminar"
                      className="p-2 text-neutral-500 hover:text-neutral-900 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>

              {/* Dates info */}
              <div className="text-xs text-neutral-500 font-light pb-3 pt-2 border-t border-neutral-100">
                Fechas: <span className="text-neutral-700">{getSelectedDates() || 'sin seleccionar (selecciónalas en el catálogo)'}</span>
              </div>

              <div className="flex flex-col gap-2 pt-2 border-t border-neutral-100">
                <Button
                  onClick={() => sendToCity('Quito')}
                  data-testid="cart-send-quito"
                  className="bg-neutral-900 hover:bg-neutral-800 text-white py-5 text-base rounded-sm"
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Consultar carrito en Quito
                </Button>
                <Button
                  onClick={() => sendToCity('Guayaquil')}
                  data-testid="cart-send-guayaquil"
                  className="bg-neutral-900 hover:bg-neutral-800 text-white py-5 text-base rounded-sm"
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Consultar carrito en Guayaquil
                </Button>
                <button
                  onClick={clear}
                  data-testid="cart-clear"
                  className="text-xs text-neutral-500 hover:text-neutral-900 transition-colors mt-1 self-center"
                >
                  Vaciar carrito
                </button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ConsultationCart;
